import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import { AuthLayout } from '../components/templates/AuthLayout';
import { Text } from '../components/atoms';
import { MarkdownContent } from '../components/markdown/MarkdownContent';
import {
  fetchLegalDocument,
  formatLegalDate,
  legalLanguageFor,
  type LegalDocument,
  type LegalDocumentId,
} from '../api/legal';
import './LegalPage.css';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';

export interface LegalPageProps {
  document: LegalDocumentId;
}

const TITLE_KEYS: Record<LegalDocumentId, string> = {
  'privacy-policy': 'legal.privacyPolicy',
  'terms-of-service': 'legal.termsOfService',
  imprint: 'legal.imprint',
};

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; doc: LegalDocument };

/**
 * Public legal document page — /privacy, /terms, /imprint (and the German
 * aliases /datenschutz, /agb, /impressum). No login required. The document
 * language follows the app locale (German for `de`, English otherwise).
 */
export function LegalPage({ document: id }: LegalPageProps) {
  const { t, i18n } = useTranslation();
  const appLanguage = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const lang = legalLanguageFor(appLanguage);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  const title = t(TITLE_KEYS[id]);

  useEffect(() => {
    globalThis.document.title = `${title} · ReefGuide`;
  }, [title]);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    fetchLegalDocument(id, lang, controller.signal)
      .then((doc) => setState({ status: 'ready', doc }))
      .catch(() => {
        if (!controller.signal.aborted) setState({ status: 'error' });
      });
    return () => controller.abort();
  }, [id, lang, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return (
    <AuthLayout title={title} width="wide">
      <div className="legal-page" data-testid="legal-page">
        {state.status === 'loading' && (
          <LiveRegion>
            <Text className="legal-page__status">{t('legal.loading')}</Text>
          </LiveRegion>
        )}

        {state.status === 'error' && (
          <LoadErrorState title={t('legal.loadError')} onRetry={retry} retryLabel={t('legal.retry')} />
        )}

        {state.status === 'ready' && (
          <>
            {(state.doc.version !== null || lang !== appLanguage.slice(0, 2)) && (
              <div className="legal-page__meta">
                {state.doc.version !== null && state.doc.effectiveDate !== null && (
                  <Text variant="muted" data-testid="legal-page-version">
                    {t('legal.versionLine', {
                      version: state.doc.version,
                      date: formatLegalDate(state.doc.effectiveDate, appLanguage),
                    })}
                  </Text>
                )}
                {lang !== appLanguage.slice(0, 2) && (
                  <Text variant="muted">{t('legal.languageNotice')}</Text>
                )}
              </div>
            )}
            <div lang={state.doc.lang}>
              <MarkdownContent source={state.doc.content} newTabLabel={t('legal.opensInNewTab')} />
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
}

export default LegalPage;
